import { withCmhubProps } from './bridge';
import { appSides, readySides, instanceKey, createNavigation, validateSide, SIDE_QUERY } from './navigation';

export const fusionContainers = apps => apps.reduce((list, app) => list.concat(
  (appSides[app.name] || []).map(side => ({ ...app, side, key: instanceKey(app.name, side) })),
), []);

/** 独立持有融合实例；不改普通系统的加载、路由和缓存逻辑。 */
export function createFusionHost({ apps, router, load, bus, onInvalidated, closeTab, onError, utilities = {} }) {
  const instances = new Map();
  const pending = new Map();
  const navigations = new Set();
  let destroyed = false;
  const getApp = key => apps.find(app => app.name === key || app.activeRule.replace(/^\/|\/$/g, '') === key);
  function prepare(app, side, container, suffix = '', extraProps = {}) {
    if (destroyed) throw new Error('融合应用宿主已销毁');
    validateSide(app.name, side);
    if (!(readySides[app.name] || []).includes(side)) throw new Error(`该微应用尚未完成${side === 'customer' ? '客' : '商'}侧融合接入：${app.name}`);
    const key = instanceKey(app.name, side) + suffix;
    const config = withCmhubProps({ ...app, name: key, container,
      props: { ...app.props, ...extraProps, _app: app.props._app } }, apps, onInvalidated, side);
    const identityContext = config.props.cmhub;
    const getIdentity = () => (destroyed ? null : identityContext.getIdentity());
    const navigation = createNavigation(router, app, side, apps, () => !destroyed && !!config.props.cmhub.getIdentity());
    navigations.add(navigation);
    const cmhub = Object.freeze({ ...identityContext, navigation, instanceKey: key, getIdentity,
      getPermissions: () => (getIdentity() ? identityContext.getPermissions() : []),
      hasPermission: code => !!getIdentity() && identityContext.hasPermission(code),
      onAuthExpired: (reportedSide, code) => { if (!destroyed) identityContext.onAuthExpired(reportedSide, code); },
    });
    const resolve = name => {
      const target = getApp(String(name).replace(/^\/+|\/+$/g, ''));
      if (!target) throw new Error('融合嵌套应用未注册');
      validateSide(target.name, side);
      return { ...target, props: { ...target.props, cmhub } };
    };
    const nestedLoad = (requested, options) => {
      if (!cmhub.getIdentity() || destroyed) throw new Error('融合应用身份已失效');
      const target = apps.find(item => item.entry === requested.entry
        && (requested.name === item.name || requested.name.startsWith(`${item.name}_embedded_`)));
      if (!target) throw new Error('融合嵌套应用未注册或入口不匹配');
      // 容器必须明确，禁止退回共享的普通应用容器。
      if (!requested.container) throw new Error('融合嵌套应用缺少独立容器');
      return mount(target, side, requested.container, `_embedded_${requested.name}`, options, requested.props);
    };
    const eventBus = Object.create(bus);
    eventBus.$emit = (event, ...args) => {
      if (!cmhub.getIdentity() || destroyed) return;
      if (event === 'lowcode_mainRouter') return navigation.push(args[0]);
      if (event === 'route-change') {
        const current = navigation.getRoute();
        if (!current) return undefined;
        const child = Object.values(args[0] || {}).find(route => route.path === current.path);
        if (!child) return undefined;
        return bus.$emit(event, { [key]: { ...child, href: router.currentRoute.fullPath } });
      }
      if (event === 'lowcode_closeCurTab' || event === 'zms_closeCurTab') {
        if (navigation.getRoute()) closeTab(args[0]);
        return undefined;
      }
      if (event.startsWith('embedded-')) throw new Error('融合嵌套应用请使用实例 util.loadMicroApp');
      return bus.$emit(event, ...args);
    };
    config.props = {
      ...config.props, cmhub, _setGlobalData: eventBus,
      util: { ...utilities, ...app.props.util, mainRouter: navigation, loadMicroApp: nestedLoad,
        resolveEmbeddedMicroApp: resolve,
        closeCurTab: cb => {
          if (router.currentRoute.query[SIDE_QUERY] === side && router.currentRoute.path.startsWith(app.activeRule)) closeTab(cb);
        } },
    };
    return { config, navigation };
  }
  function mount(app, side, container, suffix = '', options, extraProps) {
    const key = instanceKey(app.name, side) + suffix;
    if (instances.has(key)) {
      if (instances.get(key).container !== container) throw new Error('融合实例不能复用到不同容器');
      return instances.get(key).parcel;
    }
    const { config, navigation } = prepare(app, side, container, suffix, extraProps);
    let parcel;
    try { parcel = load(config, { ...options, singular: false, sandbox: true }); }
    catch (error) { navigation.dispose(); navigations.delete(navigation); throw error; }
    instances.set(key, { parcel, navigation, container });
    parcel.mountPromise.then(() => {
      if (destroyed) return parcel.unmount();
      return undefined;
    }).catch(error => {
      instances.delete(key); navigation.dispose(); navigations.delete(navigation); onError(error);
    });
    return parcel;
  }
  return {
    async ensure(app, side, waitForContainer) {
      const key = instanceKey(app.name, side);
      if (pending.has(key)) return pending.get(key);
      const task = (async () => {
        await waitForContainer();
        if (destroyed) return;
        const parcel = mount(app, side, `#${key}`);
        await parcel.mountPromise;
      })();
      pending.set(key, task);
      try { await task; } finally { pending.delete(key); }
    },
    dispose() {
      destroyed = true;
      navigations.forEach(nav => nav.dispose()); navigations.clear();
      instances.forEach(({ parcel }) => {
        if (parcel.getStatus() === 'MOUNTED') parcel.unmount().catch(onError);
      });
      instances.clear();
    },
  };
}
